// First-touch / last-touch marketing attribution.
//
// Reads UTM params + ad click-IDs (gclid/fbclid) from the URL and persists them
// to localStorage so the apply form can attach them to the Application record
// (server-side attribution + conversion stitching).
//
// Essential-tier: carries no PII and loads no third-party code, so it runs
// regardless of analytics/marketing consent. Capture happens only when the URL
// actually carries attribution params — internal navigation never clears a
// previously-captured campaign.

const FIRST_TOUCH_KEY = 'wavbee_first_touch'
const LAST_TOUCH_KEY = 'wavbee_last_touch'

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const
const CLICK_IDS = ['gclid', 'fbclid'] as const

type AttributionParam = (typeof UTM_PARAMS)[number] | (typeof CLICK_IDS)[number]

export interface TouchRecord {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  gclid?: string
  fbclid?: string
  /** Meta browser id (_fbp cookie) — read at submit, not persisted. */
  fbp?: string
  /** Meta click id (_fbc cookie) — read at submit, not persisted. */
  fbc?: string
  /** Path + query of the page where this touch was captured. */
  landing_path: string
  /** ISO timestamp of capture. */
  ts: string
}

export interface Attribution {
  first_touch: TouchRecord | null
  last_touch: TouchRecord | null
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

/** Build a touch record from the current URL, or null when no attribution params are present. */
function readTouchFromUrl(): TouchRecord | null {
  if (!isBrowser()) return null
  const params = new URLSearchParams(window.location.search)
  const record: TouchRecord = {
    landing_path: window.location.pathname + window.location.search,
    ts: new Date().toISOString(),
  }
  let found = false
  const keys: AttributionParam[] = [...UTM_PARAMS, ...CLICK_IDS]
  for (const key of keys) {
    const value = params.get(key)
    if (value) {
      record[key] = value
      found = true
    }
  }
  return found ? record : null
}

function readStored(key: string): TouchRecord | null {
  if (!isBrowser()) return null
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as TouchRecord) : null
  } catch {
    return null
  }
}

/**
 * Capture attribution from the current URL into localStorage.
 * - first touch: written once, never overwritten.
 * - last touch: overwritten on every attributed visit.
 * No-op when the URL carries no UTM / click-ID params.
 */
export function captureAttribution(): void {
  const touch = readTouchFromUrl()
  if (!touch) return
  try {
    if (!readStored(FIRST_TOUCH_KEY)) {
      localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch))
    }
    localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(touch))
  } catch {
    /* ignore quota errors */
  }
}

/**
 * The Meta Pixel's own identifier cookies. `_fbp` exists for every visitor the
 * pixel has seen (marketing consent granted); `_fbc` when they arrived via an
 * ad click. Both materially raise Conversions API event match quality, so they
 * are read fresh at submit time rather than only at URL-capture time.
 */
export function readMetaCookieIds(): Pick<TouchRecord, 'fbp' | 'fbc'> {
  if (typeof document === 'undefined') return {}
  const read = (name: string): string | undefined => {
    const row = document.cookie.split('; ').find((r) => r.startsWith(`${name}=`))
    return row ? decodeURIComponent(row.slice(name.length + 1)) : undefined
  }
  const ids: Pick<TouchRecord, 'fbp' | 'fbc'> = {}
  const fbp = read('_fbp')
  if (fbp) ids.fbp = fbp
  const fbc = read('_fbc')
  if (fbc) ids.fbc = fbc
  return ids
}

/**
 * Read the persisted first/last touch records, for attaching to a form submit.
 * The last touch is enriched with the Meta cookie ids (fbp/fbc) at read time —
 * for organic visitors with no stored touch, a minimal touch record is built
 * so the server-side Conversions API pings can still match the browser.
 */
export function getAttribution(): Attribution {
  const first = readStored(FIRST_TOUCH_KEY)
  const last = readStored(LAST_TOUCH_KEY)
  const ids = readMetaCookieIds()
  if (!ids.fbp && !ids.fbc) return { first_touch: first, last_touch: last }

  const base: TouchRecord = last ?? {
    landing_path: isBrowser() ? window.location.pathname + window.location.search : '',
    ts: new Date().toISOString(),
  }
  return { first_touch: first, last_touch: { ...base, ...ids } }
}
